import { useEffect, useRef } from 'react';
import { generatePath, useLocation, useNavigate, useNavigationType } from 'react-router';
import { useDispatch } from 'react-redux';
import { fetchJobsWithAdjustmts } from '~/actions';
import type { IJob } from '~/models';
import { routes } from '~/routes';
import { getRedirectTarget } from '~/utils/AdjustmentUtil';

/** `location.state` the End of Day screens set on their way back to this route. */
interface ICompletedRedirectLocationState {
  /**
   * Set by the feedback screens when they navigate back here, so landing from
   * feedback does not bounce straight into feedback again.
   */
  isPrevScreenFeedback?: boolean;
}

/**
 * The End of Day / rating redirect, as a route entry guard for the redesigned
 * COMPLETED page.
 *
 * `JobDetails.js` runs this in `componentDidUpdate` for **any** job with
 * `!isFeedbackSubmited` — it is not gated on status, so it fires for `complete`
 * too, and the rating it leads to is what releases the professional's payment.
 * A redesigned page that dropped it would silently cost the office the prompt.
 *
 * The guard is deliberately **duplicated** rather than extracted from the legacy
 * class: with the flag off, `JobDetails.js` must show no diff at all. The
 * duplication dies with the legacy screen at the end of the rollout. What is
 * *not* duplicated is {@link getRedirectTarget} — both call the same shared util,
 * so the two paths can never disagree about where a given set of adjustments
 * leads.
 *
 * The bounce is intended behaviour, confirmed 2026-09-09: it is not softened,
 * delayed or made dismissible here. The consequence is that the COMPLETED page is
 * the **post-rating** view of a shift — a freshly finished shift is meant to hit
 * rating first and reach this page only afterwards. QA needs a `complete` shift
 * whose feedback is already submitted, or the redirect does its job and the page
 * looks unbuilt.
 *
 * All five of the legacy guards are reproduced, and each one earns its place:
 *
 * 1. **Not a POP navigation.** Without it the back button becomes a loop: leave
 *    feedback, land here, get thrown back into feedback.
 * 2. **`!job.isFeedbackSubmited`** — nothing to collect once it is in.
 * 3. **`!isLoading`** — never redirect while `adjustments` is still unresolved,
 *    or the target is chosen from a half-loaded job.
 * 4. **A fired-once latch.** A `useRef`, not state: state would re-render and,
 *    since the legacy latch is read in the same pass that sets it, a state latch
 *    fires the redirect twice before the first value lands.
 * 5. **`!location.state?.isPrevScreenFeedback`** — the feedback screen sets this
 *    on the way back.
 *
 * @param job The loaded shift. The gate has already resolved it.
 * @param isLoading Whether the shift is still loading. The gate renders no page
 *   until the fetch settles, so this is structurally `false` on the live path;
 *   the parameter is kept because guard 3 is part of the behaviour being
 *   reproduced, and it holds for any future caller that loads its own job.
 */
export function useCompletedFeedbackRedirect(job?: IJob | null, isLoading = false): void {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const dispatch = useDispatch();

  /*
   * Guard 4. A ref rather than state because this must latch *within* the render
   * pass that fires the redirect: `setState` would not be visible to the next
   * effect run, so a second one would fire before React re-rendered.
   */
  const hasRedirected = useRef(false);

  const isPrevScreenFeedback = Boolean(
    (location.state as ICompletedRedirectLocationState | null)?.isPrevScreenFeedback,
  );

  useEffect(() => {
    if (
      navigationType === 'POP' ||
      !job ||
      job.isFeedbackSubmited ||
      isLoading ||
      hasRedirected.current ||
      isPrevScreenFeedback
    ) {
      return;
    }

    const { id: jobId, adjustments } = job;

    /*
     * The legacy `handleRedirectFeedback` bails before latching when either is
     * missing, so a job whose adjustments have not arrived stays on the page and
     * is reconsidered on the next pass rather than being spent.
     */
    if (!jobId || !adjustments) {
      return;
    }

    /*
     * Latched before the target is resolved, exactly as the legacy does: a
     * `null` target means "stay here", and it must still consume the one attempt
     * so a later re-render cannot re-run the decision.
     */
    hasRedirected.current = true;

    const redirectTarget = getRedirectTarget(adjustments);

    if (redirectTarget === 'feedback-rating') {
      navigate(generatePath(routes.jobSummaryRating, { jobId }));
      return;
    }

    if (redirectTarget === 'feedback') {
      navigate(generatePath(routes.jobSummary, { jobId }));
      return;
    }

    if (redirectTarget === 'adjustment') {
      // Refetch only — the legacy pulls the adjustments list and stays put.
      dispatch(fetchJobsWithAdjustmts());
    }
  }, [job, isLoading, navigationType, isPrevScreenFeedback, navigate, dispatch]);
}
